import { MutationFunctionOptions, useMutation } from "@apollo/client";
import { Box, Flex, Text } from "@govlaunch/web";
import React, { useCallback, useContext, useState } from "react";
import { FormattedMessage } from "react-intl";
import { AuthContext, IAuthContext } from "~/components/auth/Auth";
import SignInMutation from "~/lib/mutations/SignInMutation";
import { ISignInMutation, ISignInMutationVariables } from "~/lib/mutations/__generated__/SignInMutation.generated";
import SelfieQuery from "~/lib/queries/SelfieQuery";
import { useIntlLocale } from "~/lib/withIntl";
import globalOnSignIn from "~/src/components/auth/globalOnSignIn";
import OAuthFacebook from "~/src/components/auth/OAuthFacebook";
import OAuthLinkedIn from "~/src/components/auth/OAuthLinkedIn";
import OAuthTwitter from "~/src/components/auth/OAuthTwitter";
import SignInBlocked from "~/src/components/auth/SignInBlocked";
import SignInSwitchLink from "~/src/components/auth/SignInSwitchLink";

interface ISignInPageMobileProps {
  signingIn?: boolean;
  inviteToken?: string | null;
}

export default function SignInPageMobile({ signingIn = true, inviteToken }: ISignInPageMobileProps) {
  const [isBlocked, setIsBlocked] = useState(false);
  const { login } = useContext<IAuthContext>(AuthContext);
  const { locale, locales, changeLocale } = useIntlLocale();

  const [signIn] = useMutation<ISignInMutation, ISignInMutationVariables>(SignInMutation, {
    onCompleted: ({ signIn: data }) => {
      if (data && data.tokens && data.tokens.token) {
        login(data.tokens.token);

        if (
          data.user &&
          data.user.language &&
          locale !== data.user.language &&
          locales.map(({ id }) => id).includes(data.user.language)
        ) {
          changeLocale(data.user.language);
        }
      }
    },
    refetchQueries: [
      "Homepage",
      "SearchProjects",
      "ProjectsByGovernment",
      "ProjectsByIds",
      "ProjectBySlug",
      "StoryBySlug",
    ],
    update: (proxy, { data }) => {
      if (data && data.signIn) {
        proxy.writeQuery({
          query: SelfieQuery,
          variables: {
            token: inviteToken || null,
          },
          data: {
            __typename: "Query",
            selfie: {
              ...data.signIn.user,
            },
          },
        });
      }
    },
  });

  const createOnSignInHandler = useCallback((provider: "linkedin" | "twitter" | "facebook") => {
    return async (options?: MutationFunctionOptions<ISignInMutation, ISignInMutationVariables> | undefined) => {
      const signInResult = await signIn(options);
      if (signInResult.data?.signIn) {
        globalOnSignIn(provider, signInResult.data?.signIn);
      }
      return signInResult;
    };
  }, []);

  return (
    <Flex
      position="relative"
      direction="column"
      width="100%"
      height="100vh"
      backgroundImage="url('/static/signin/signin-background.png')"
      backgroundPosition="center center"
      backgroundRepeat="repeat-y"
      backgroundSize="cover"
    >
      <Box ml="40px">
        <Text color="white" fontSize="xl" fontWeight="semibold" lineHeight="26px" textTransform="uppercase" mt="44px">
          {signingIn ? (
            <FormattedMessage defaultMessage="WELCOME BACK!" id="8VGC7R" />
          ) : (
            <FormattedMessage defaultMessage="JOIN FOR FREE" id="kf9pAr" />
          )}
        </Text>
        <Text color="white" fontSize="3xl" fontWeight="semibold" lineHeight="38px" mt="22px">
          {signingIn ? (
            <FormattedMessage defaultMessage="Log in to Govlaunch" id="iqah/X" />
          ) : (
            <FormattedMessage defaultMessage="Sign up for Govlaunch" id="rT5edK" />
          )}
        </Text>

        <SignInSwitchLink signIn={signingIn} mt="20px" color="white" />
      </Box>

      <Box mt="26px" bg="white" py="30px" px="32px">
        {isBlocked ? (
          <SignInBlocked />
        ) : (
          <Flex direction="column">
            <OAuthLinkedIn
              signIn={createOnSignInHandler("linkedin")}
              inviteToken={inviteToken}
              setBlocked={setIsBlocked}
            />
            {false && (
              <OAuthTwitter
                signIn={createOnSignInHandler("twitter")}
                inviteToken={inviteToken}
                setBlocked={setIsBlocked}
                mt="24px"
              />
            )}
            {false && (
              <OAuthFacebook
                signIn={createOnSignInHandler("facebook")}
                inviteToken={inviteToken}
                setBlocked={setIsBlocked}
                mt="24px"
              />
            )}
          </Flex>
        )}
      </Box>
    </Flex>
  );
}
